import TuneIcon from "@mui/icons-material/Tune";
import {
  Box,
  Drawer,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useSearch } from "contexts/search/SearchContextProvider";
import {
  useSearchViewedNotification,
  useTrackSearchIdInUrl,
} from "contexts/search/effects";
import { useNotifyStatus } from "contexts/status";
import { useTenantTranslation } from "hooks/formatters";
import { hotkeys, useSearchPageHotkeys } from "hooks/hotkeys";
import { PeriodVariant, usePeriodVariant } from "hooks/periodIndex";
import { usePlanId, useRunSearch, useSearchName } from "hooks/search";
import { useIsAdmin } from "hooks/settings";
import { useShowMaterialOverrides } from "hooks/tenantPermissions";
import { useIsMobile } from "hooks/useIsMobile";
import { useStringQueryParamState } from "hooks/useStringQueryParamState";
import { mapLoadedUnpack } from "models/loaded";
import React, { useCallback } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useTabName } from "../common/TabTitle";
import { PeriodTabs } from "../common/periodTabs/tabs";
import { ConstraintsPageContent } from "../constraints/constraintsPage";
import { PANEL_WIDTH, PanelLayout } from "./common/PanelLayout";
import { HistoryPanel } from "./input/HistoryPanel";
import { InputPanel } from "./input/InputPanel";
import { InventoryEditor } from "./input/inputEditors/InventoryEditor";
import { MaterialOverridesEditor } from "./input/inputEditors/MaterialOverridesEditor";
import { TargetInventoryEditor } from "./input/inputEditors/TargetInventoryEditor";
import { AvailabilitiesEditor } from "./input/inputEditors/availabilities/AvailabilitiesEditor";
import { ProductionEditor } from "./input/inputEditors/production/productionEditor";
import { TargetBasketsEditor } from "./input/inputEditors/targetBasketsEditor";
import { RunSearchButtonGroup } from "./output/RunSearchButtonGroup";
import { SearchActionsButton } from "./output/SearchActionsButton";
import { SearchResults } from "./output/SearchResults";
import { PlanPanel } from "./output/planPanel";
import { Panel } from "./types";

const SearchSaveStatus = () => {
  const { t } = useTenantTranslation();
  const status = useSearch().parameters.server.status;
  const theme = useTheme();
  return (
    <Typography
      sx={{
        marginLeft: "auto !important",
        color: theme.palette.text.secondary,
      }}
    >
      {status !== "success" ? t("saving") : t("saved")}
    </Typography>
  );
};

interface BodyProps {
  setShowHistory: (show: boolean) => void;
  showHistory: boolean;
  setPanel: (panel: Panel) => void;
  panel: Panel;
  showInputsDrawer: boolean;
  setShowInputsDrawer: (show: boolean) => void;
}

const Body = ({
  setShowHistory,
  showHistory,
  setPanel,
  panel,
  showInputsDrawer,
  setShowInputsDrawer,
}: BodyProps) => {
  const { t } = useTenantTranslation();
  const isAdmin = useIsAdmin();
  const showMaterialOverrides = useShowMaterialOverrides();
  const isMobile = useIsMobile();
  useSearchPageHotkeys(panel, setPanel);
  useTabName(mapLoadedUnpack(useSearchName(), (name) => name));

  const cancelPanel = () => {
    setPanel("None");
  };

  const periodVariant = usePeriodVariant();

  const openInventoryOnHand =
    panel === "Inventory" && periodVariant === PeriodVariant.Schedule;
  const openAvailableScrap = panel === "Availabilities";
  const openForecastProduction = panel === "Production";
  const openTargetInventory = panel === "TargetInventory";
  const openProductionBaskets =
    panel === "TargetBaskets" && periodVariant === PeriodVariant.Schedule;
  const openConstraints = panel === "Constraints";
  const openMaterialOverrides =
    panel === "MaterialOverrides" && (isAdmin || showMaterialOverrides);

  useHotkeys(
    hotkeys.constraintsPage,
    () => {
      setPanel("None");
    },
    {
      enabled: openConstraints,
    }
  );

  const inputPanelContent = (
    <>
      <InputPanel setPanel={setPanel} />
      <HistoryPanel showHistory={showHistory} setShowHistory={setShowHistory} />
    </>
  );

  return (
    <Grid container sx={{ height: 0, flexGrow: 1 }}>
      {!isMobile && (
        <Grid
          sx={{
            height: "100%",
            position: "relative",
            width: `${PANEL_WIDTH}px`,
          }}
        >
          {inputPanelContent}
        </Grid>
      )}
      {isMobile ? (
        <Drawer
          anchor="left"
          open={showInputsDrawer}
          onClose={() => {
            setShowInputsDrawer(false);
          }}
          slotProps={{
            backdrop: {
              sx: { top: "var(--app-bar-height, 0px)" },
            },
            paper: {
              sx: {
                width: "85vw",
                maxWidth: "320px",
                top: "var(--app-bar-height, 0px)",
                height: "calc(100% - var(--app-bar-height, 0px))",
              },
            },
          }}
        >
          <Box sx={{ height: "100%", position: "relative" }}>
            {inputPanelContent}
          </Box>
        </Drawer>
      ) : null}
      <Grid size="grow" sx={{ height: "100%", position: "relative" }}>
        <SearchResults />
        <PlanPanel width="100%" />
        <PanelLayout
          title={t("constraints")}
          open={openConstraints}
          doClose={cancelPanel}
        >
          <ConstraintsPageContent />
        </PanelLayout>
        <PanelLayout
          title={t("inventoryInHand")}
          description={t("inventoryInHandDescription")}
          open={openInventoryOnHand}
          doClose={cancelPanel}
          topRight={<SearchSaveStatus />}
        >
          <InventoryEditor />
        </PanelLayout>
        <PanelLayout
          title={t("availableScrap")}
          description={t("availableScrapDescription")}
          open={openAvailableScrap}
          doClose={cancelPanel}
          topRight={<SearchSaveStatus />}
        >
          <AvailabilitiesEditor />
        </PanelLayout>
        <PanelLayout
          title={t("production")}
          description={t("productionDescription")}
          open={openForecastProduction}
          doClose={cancelPanel}
          topRight={<SearchSaveStatus />}
        >
          <ProductionEditor />
        </PanelLayout>
        <PanelLayout
          title={t("targetInventory")}
          description={t("targetInventoryDescription")}
          open={openTargetInventory}
          doClose={cancelPanel}
          topRight={<SearchSaveStatus />}
        >
          <TargetInventoryEditor />
        </PanelLayout>
        <PanelLayout
          title={t("materialOverrides")}
          description={t("materialOverridesDescription")}
          open={openMaterialOverrides}
          doClose={cancelPanel}
          topRight={<SearchSaveStatus />}
        >
          <MaterialOverridesEditor />
        </PanelLayout>
        <PanelLayout
          title={t("productionBaskets")}
          description={t("productionBasketsDescription")}
          open={openProductionBaskets}
          doClose={cancelPanel}
          topRight={<SearchSaveStatus />}
        >
          <TargetBasketsEditor />
        </PanelLayout>
      </Grid>
    </Grid>
  );
};

interface RunSearchButtonProps {
  onSubmit: () => void;
  loading: boolean;
}

const RunSearchButton = ({ onSubmit, loading }: RunSearchButtonProps) => {
  return <RunSearchButtonGroup onSubmit={onSubmit} loading={loading} />;
};

export const SearchPage = () => {
  useSearchViewedNotification();
  useTrackSearchIdInUrl();

  const [showHistory, setShowHistory] = React.useState(false);
  const [showInputsDrawer, setShowInputsDrawer] = React.useState(false);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [panel, setPanel] = useStringQueryParamState<Panel>("panel", "None");
  const runSearch = useRunSearch();
  const { t } = useTenantTranslation();
  const notifyStatus = useNotifyStatus();
  const isMobile = useIsMobile();
  const planId = usePlanId();
  const isPlanOpen = planId !== null;

  const handleStartRunSearch = useCallback(() => {
    setSearchLoading(true);
    setPanel("None");
    setShowInputsDrawer(false);
    try {
      runSearch();
    } catch {
      notifyStatus({
        type: "error",
        text: t("optimisationErrorDescription"),
      });
    } finally {
      setSearchLoading(false);
    }
  }, [
    runSearch,
    notifyStatus,
    setPanel,
    t,
    setSearchLoading,
    setShowInputsDrawer,
  ]);

  const handleSetPanel = useCallback(
    (panel: Panel) => {
      setPanel(panel);
      if (panel !== "None") {
        setShowInputsDrawer(false);
      }
    },
    [setPanel]
  );

  const showSearchControls = !(isMobile && isPlanOpen);

  return (
    <PeriodTabs
      trailingComponent={
        showSearchControls ? (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              justifyContent: isMobile ? "space-between" : undefined,
            }}
          >
            {isMobile ? (
              <IconButton
                onClick={() => {
                  setShowInputsDrawer(true);
                }}
                size="small"
              >
                <TuneIcon />
              </IconButton>
            ) : null}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <RunSearchButton
                onSubmit={handleStartRunSearch}
                loading={searchLoading}
              />
              <Box sx={{ ml: 1 }}>
                <SearchActionsButton
                  setShowHistory={setShowHistory}
                  setPanel={handleSetPanel}
                />
              </Box>
            </Box>
          </Box>
        ) : undefined
      }
    >
      <Body
        showHistory={showHistory}
        setShowHistory={setShowHistory}
        panel={panel}
        setPanel={handleSetPanel}
        showInputsDrawer={showInputsDrawer}
        setShowInputsDrawer={setShowInputsDrawer}
      />
    </PeriodTabs>
  );
};
